import { useEffect, useRef, useState } from "react";
import type {
  ElementFallbackResponse,
  ElementSourceResponse,
  InventionElementResponse,
} from "@patent-studio/api-types";
import { plural } from "@/lib/plural";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { OpenDocument, citedPages } from "../documents/OpenDocument";
import type { Openable } from "./ElementCard";
import { AddRungForm, KIND_LABEL } from "./AddRungForm";
import type { AddedRung } from "./AddRungForm";
import { MarkedWords, TermField, TermNotes, useTerm } from "./terms";
import { OpenQuestionOnly, Overlaps } from "./FindingMarks";
import type { RungFindings } from "./FindingMarks";

export interface LadderWrites {
  /** Resolves when the rung is stored. A rejection leaves the form standing. */
  onAdd: (rung: AddedRung) => Promise<unknown>;
  /** Resolves when the wording is stored. A rejection leaves the editor open. */
  onAmend: (fallbackId: string, text: string) => Promise<unknown>;
  /** Resolves when the rung's term is stored. */
  onName: (fallbackId: string, term: string) => Promise<unknown>;
  onRemove: (fallbackId: string) => void;
}

/** One source of a rung, as the element's own line reads it. */
function citation(
  source: ElementSourceResponse,
  files: Openable,
): {
  label: string;
  fileId: string | null;
} {
  const pages = citedPages(
    source.locations,
    source.file_id == null ? undefined : files.get(source.file_id),
  );
  const where = pages.length > 0 ? ` (${pages.join(", ")})` : "";
  return {
    label: `${source.document_label}${where}`,
    fileId: source.file_id ?? null,
  };
}

/**
 * Where a rung was quoted from, opening the file where it is on the task and
 * text where it is not, with the label beside it where no passage teaches it.
 */
function RungProvenance({
  rung,
  uncertainOnly,
  openable,
  onOpen,
}: {
  rung: ElementFallbackResponse;
  uncertainOnly: readonly string[];
  openable: Openable;
  onOpen: (fileId: string) => void;
}) {
  const sources = rung.sources;
  if (sources.length === 0) return null;
  return (
    <>
      <span
        data-testid={`rung-source-${rung.fallback_id}`}
        className="ml-2 font-mono text-[10px] text-muted-foreground"
      >
        {sources
          .map((source) => citation(source, openable))
          .map(({ label, fileId }, i) => (
            <span key={`${String(i)}:${label}`}>
              {i > 0 && " · "}
              {fileId !== null && openable.has(fileId) ? (
                <OpenDocument
                  onOpen={() => {
                    onOpen(fileId);
                  }}
                >
                  {label}
                </OpenDocument>
              ) : (
                label
              )}
            </span>
          ))}
      </span>
      <OpenQuestionOnly
        id={rung.fallback_id}
        uncertainOnly={uncertainOnly}
        keptReasons={rung.kept_reasons}
        className="ml-2"
      />
    </>
  );
}

/** The inline editor over a rung's words and its term. */
function RungEditor({
  initial,
  busy,
  onSave,
  onCancel,
}: {
  initial: string;
  busy: boolean;
  /** The wording, and the term marked, or null where none was. */
  onSave: (text: string, term: string | null) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [term, setTerm] = useState("");
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);
  return (
    <div className="flex-1">
      <Textarea
        size="dense"
        ref={fieldRef}
        aria-label="Fallback wording"
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        rows={1}
        className="text-[12px]"
      />
      <TermField label="Fallback term" text={draft} onChange={setTerm} />
      <div className="mt-1.5 flex gap-2">
        <Button
          type="button"
          variant="secondary"
          size="dense"
          disabled={busy || draft.trim() === ""}
          onClick={() => {
            onSave(draft.trim(), term === "" ? null : term);
          }}
        >
          Save
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="dense"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** What may be done to a rung: reword it, or drop it. */
function RungActions({
  text,
  busy,
  onAmend,
  onRemove,
}: {
  text: string;
  busy: boolean;
  onAmend: () => void;
  onRemove: () => void;
}) {
  return (
    <>
      <Button
        type="button"
        variant="quiet"
        size="dense"
        className="ml-2"
        aria-label={`Amend fallback ${text}`}
        onClick={onAmend}
      >
        Amend
      </Button>
      <Button
        type="button"
        variant="quiet"
        size="dense"
        aria-label={`Remove fallback ${text}`}
        disabled={busy}
        onClick={onRemove}
      >
        Remove
      </Button>
    </>
  );
}

/** One rung: its kind, its words or the editor over them, where it was quoted from, and what may be done to it. */
function Rung({
  rung,
  findings,
  busy,
  writes,
  openable,
  onOpenSource,
  children,
}: {
  rung: ElementFallbackResponse;
  /** What the server found of the element's rungs, by rung id. */
  findings: RungFindings;
  busy: boolean;
  writes: LadderWrites;
  openable: Openable;
  onOpenSource: (fileId: string) => void;
  children?: React.ReactNode;
}) {
  const [amending, setAmending] = useState(false);
  const id = rung.fallback_id;
  const term = useTerm(id);
  return (
    <li data-testid={`rung-${id}`} className="mt-1">
      <div className="flex items-start gap-2">
        <MicroLabel size="sm" className="mt-[3px] shrink-0">
          {KIND_LABEL[rung.kind]}
        </MicroLabel>
        {amending ? (
          <RungEditor
            initial={rung.text}
            busy={busy}
            onSave={(text, marked) => {
              // The wording first, and only where it moved, as the element's
              // editor does: the term is read against the words it stands in.
              const reworded =
                text === rung.text
                  ? Promise.resolve()
                  : writes.onAmend(id, text);
              void reworded
                .then(() =>
                  marked === null ? undefined : writes.onName(id, marked),
                )
                .then(
                  () => {
                    setAmending(false);
                  },
                  () => undefined,
                );
            }}
            onCancel={() => {
              setAmending(false);
            }}
          />
        ) : (
          <span className="text-[12px] leading-[1.5]">
            <span>
              <MarkedWords text={rung.text} term={term} />
            </span>
            <TermNotes term={term} />
            <RungProvenance
              rung={rung}
              uncertainOnly={findings.uncertainOnly}
              openable={openable}
              onOpen={onOpenSource}
            />
            <Overlaps id={id} overlapping={findings.overlapping} />
            <RungActions
              text={rung.text}
              busy={busy}
              onAmend={() => {
                setAmending(true);
              }}
              onRemove={() => {
                writes.onRemove(id);
              }}
            />
          </span>
        )}
      </div>
      {children}
    </li>
  );
}

/** The control that opens the add form at one place in the ladder, and the form once open. */
function AddAt({
  parentId,
  busy,
  onAdd,
  label,
}: {
  parentId: string | null;
  busy: boolean;
  onAdd: (rung: AddedRung) => Promise<unknown>;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button
        type="button"
        variant="quiet"
        size="dense"
        className="mt-1"
        onClick={() => {
          setOpen(true);
        }}
      >
        {label}
      </Button>
    );
  }
  return (
    <AddRungForm
      parentId={parentId}
      busy={busy}
      onAdd={onAdd}
      onClose={() => {
        setOpen(false);
      }}
    />
  );
}

/** What sits under one class, once it is opened: its members and values, and the control that adds one. */
function ClassRungs({
  classRung,
  members,
  findings,
  busy,
  writes,
  openable,
  onOpenSource,
}: {
  classRung: ElementFallbackResponse;
  members: readonly ElementFallbackResponse[];
  findings: RungFindings;
  busy: boolean;
  writes: LadderWrites;
  openable: Openable;
  onOpenSource: (fileId: string) => void;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="ml-4">
      <Button
        type="button"
        variant="quiet"
        size="dense"
        aria-expanded={shown}
        aria-label={`${shown ? "Close" : "Open"} class ${classRung.text}`}
        onClick={() => {
          setShown((was) => !was);
        }}
      >
        {plural(members.length, "member or value")}
        {shown ? " ▾" : " ▸"}
      </Button>
      {shown && (
        <>
          <ul>
            {members.map((child) => (
              <Rung
                key={child.fallback_id}
                rung={child}
                findings={findings}
                busy={busy}
                writes={writes}
                openable={openable}
                onOpenSource={onOpenSource}
              />
            ))}
          </ul>
          <AddAt
            parentId={classRung.fallback_id}
            busy={busy}
            onAdd={writes.onAdd}
            label={`Add under ${classRung.text}`}
          />
        </>
      )}
    </div>
  );
}

/** The ladder opened: its top rungs, each class with what sits under it, and the control that adds one. */
function LadderRungs({
  rungs,
  findings,
  busy,
  writes,
  openable,
  onOpenSource,
}: {
  rungs: InventionElementResponse["fallbacks"];
  findings: RungFindings;
  busy: boolean;
  writes: LadderWrites;
  openable: Openable;
  onOpenSource: (fileId: string) => void;
}) {
  const top = rungs.filter((rung) => rung.parent_id == null);
  return (
    <div className="mt-1 pl-1">
      <ul>
        {top.map((rung) => (
          <Rung
            key={rung.fallback_id}
            rung={rung}
            findings={findings}
            busy={busy}
            writes={writes}
            openable={openable}
            onOpenSource={onOpenSource}
          >
            {rung.kind === "class" && (
              <ClassRungs
                classRung={rung}
                members={rungs.filter(
                  (child) => child.parent_id === rung.fallback_id,
                )}
                findings={findings}
                busy={busy}
                writes={writes}
                openable={openable}
                onOpenSource={onOpenSource}
              />
            )}
          </Rung>
        ))}
      </ul>
      <AddAt
        parentId={null}
        busy={busy}
        onAdd={writes.onAdd}
        label="Add fallback"
      />
    </div>
  );
}

/**
 * The ladder beneath an element: its classes, members and values, folded until
 * the attorney opens it, with each class opening in turn to what sits under it.
 *
 * Folded by default because the element is what the page is about; the ladder
 * is what the claims may retreat to, and it is read when the attorney is
 * thinking about retreats.
 */
export function ElementLadder({
  element,
  busy,
  writes,
  openable,
  onOpenSource,
  addingFirst = false,
  onAddingFirstDone,
}: {
  element: InventionElementResponse;
  busy: boolean;
  writes: LadderWrites;
  openable: Openable;
  onOpenSource: (fileId: string) => void;
  /** The card's "Add fallback" was pressed on an element with none: the form, alone. */
  addingFirst?: boolean;
  onAddingFirstDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rungs = element.fallbacks;
  // An element with no fallbacks draws no ladder at all, as the design draws
  // a card; its first one is added from the card's acts.
  if (rungs.length === 0) {
    return addingFirst ? (
      <div
        data-testid={`ladder-${element.element_id}`}
        className="mt-2 border-t border-dashed border-border pt-2"
      >
        <AddRungForm
          parentId={null}
          busy={busy}
          onAdd={writes.onAdd}
          onClose={() => onAddingFirstDone?.()}
        />
      </div>
    ) : null;
  }
  const findings: RungFindings = {
    uncertainOnly: element.uncertain_only,
    overlapping: element.overlapping,
  };
  return (
    <div
      data-testid={`ladder-${element.element_id}`}
      className="mt-2 border-t border-dashed border-border pt-2"
    >
      <Button
        type="button"
        variant="quiet"
        size="dense"
        aria-expanded={open}
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {plural(rungs.length, "fallback")}
        {open ? " ▾" : " ▸"}
      </Button>
      {open && (
        <LadderRungs
          rungs={rungs}
          findings={findings}
          busy={busy}
          writes={writes}
          openable={openable}
          onOpenSource={onOpenSource}
        />
      )}
    </div>
  );
}
